import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Calendar, TrendingUp, Sparkles, MessageCircle, Video, BarChart3 } from 'lucide-react';
import axios from 'axios';
import { getCachedSiteSettings, setCachedSiteSettings } from '../utils/siteSettingsCache';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const iconMap = { Calendar, TrendingUp, Sparkles, MessageCircle, Video, BarChart3 };

function HeroSection({ settings, navigate }) {
  const heroRef = useRef(null);
  const [mousePos, setMousePos] = useState({ x: 0.5, y: 0.5 });
  const whatsappLink = `https://wa.me/${(settings?.whatsapp_number || '+905444720713').replace(/[^0-9]/g, '')}`;

  const handleMouseMove = useCallback((e) => {
    if (!heroRef.current) return;
    const rect = heroRef.current.getBoundingClientRect();
    setMousePos({
      x: (e.clientX - rect.left) / rect.width,
      y: (e.clientY - rect.top) / rect.height,
    });
  }, []);

  return (
    <section
      ref={heroRef}
      onMouseMove={handleMouseMove}
      className="relative min-h-screen flex items-center justify-center px-6 pt-20 overflow-hidden"
      style={{
        backgroundImage: 'url(https://static.prod-images.emergentagent.com/jobs/d9bd63b7-d6a6-43cd-a29b-1a59dd534490/images/d9d097cd520df86f7eb413c9bbf5b0402cd9aed917b105693a21b63261712c59.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      <div className="absolute inset-0 bg-black/50"></div>

      {/* Neon mor ışık efektleri - mouse ile hareket eder */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className="absolute w-[700px] h-[700px] rounded-full transition-all duration-300 ease-out"
          style={{
            background: 'radial-gradient(circle, rgba(176,38,255,0.55) 0%, rgba(176,38,255,0.15) 40%, transparent 70%)',
            left: `${mousePos.x * 100 - 18}%`,
            top: `${mousePos.y * 100 - 18}%`,
            transform: 'translate(-50%, -50%)',
            filter: 'blur(60px)',
          }}
        />
        <div
          className="absolute w-[500px] h-[900px] transition-all duration-500 ease-out"
          style={{
            background: 'linear-gradient(180deg, rgba(176,38,255,0.6) 0%, rgba(176,38,255,0.15) 50%, transparent 100%)',
            left: `${mousePos.x * 50 + 15}%`,
            top: '-25%',
            transform: `rotate(${(mousePos.x - 0.5) * 25}deg)`,
            filter: 'blur(30px)',
          }}
        />
        <div
          className="absolute w-[400px] h-[800px] transition-all duration-700 ease-out"
          style={{
            background: 'linear-gradient(180deg, rgba(200,80,255,0.5) 0%, rgba(176,38,255,0.1) 60%, transparent 100%)',
            right: `${(1 - mousePos.x) * 35 + 5}%`,
            top: '-15%',
            transform: `rotate(${(0.5 - mousePos.x) * 20}deg)`,
            filter: 'blur(35px)',
          }}
        />
        <div
          className="absolute w-[600px] h-[600px] rounded-full transition-all duration-500 ease-out"
          style={{
            background: 'radial-gradient(circle, rgba(176,38,255,0.4) 0%, transparent 60%)',
            left: `${(1 - mousePos.x) * 70}%`,
            bottom: `${mousePos.y * 25}%`,
            filter: 'blur(50px)',
          }}
        />
      </div>

      <div className="relative z-10 text-center max-w-5xl mx-auto">
        <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#B026FF]" style={{ fontFamily: 'Outfit, sans-serif' }}>Premium Mentorluk Platformu</span>
        <h2 className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-black mb-6 mt-4" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="hero-heading">
          {settings?.hero_title || "Karga Akademi ile Sınırları Aş"}
        </h2>
        <p className="text-base sm:text-lg leading-relaxed text-[#A1A1AA] mb-8 max-w-2xl mx-auto">
          {settings?.hero_subtitle || "Sana özel premium koçluk ve takip sistemi."}
        </p>
        <div className="flex flex-col items-center justify-center gap-4 sm:hidden">
          <Button onClick={() => navigate('/register')} size="lg" className="bg-[#B026FF] hover:bg-[#C966FF] text-white shadow-lg shadow-[#B026FF]/30 transition-all hover:-translate-y-1 px-8" data-testid="hero-register-btn">
            Kaydol
          </Button>
          <Button onClick={() => navigate('/login')} size="lg" variant="outline" className="border-white/10 hover:border-[#B026FF]/50 text-white hover:text-[#B026FF] transition-all" data-testid="hero-login-btn">
            Giriş Yap
          </Button>
        </div>
        <div className="hidden sm:flex sm:flex-row items-center justify-center gap-4">
          <Button onClick={() => navigate('/packages')} size="lg" className="bg-[#B026FF] hover:bg-[#C966FF] text-white shadow-lg shadow-[#B026FF]/30 transition-all hover:-translate-y-1 px-8" data-testid="hero-cta-btn">
            Satın Al
          </Button>
          <Button onClick={() => window.open(whatsappLink, '_blank')} size="lg" variant="outline" className="border-white/10 hover:border-[#B026FF]/50 text-white hover:text-[#B026FF] transition-all" data-testid="hero-whatsapp-btn">
            Koçlarımızla Ücretsiz Görüş
          </Button>
        </div>
      </div>
    </section>
  );
}

export default function LandingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [settings, setSettings] = useState(() => getCachedSiteSettings());

  useEffect(() => {
    axios
      .get(`${API}/site-settings`)
      .then((r) => {
        const nextSettings = r.data || {};
        setSettings(nextSettings);
        setCachedSiteSettings(nextSettings);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (user) {
      if (user.role === 'admin') navigate('/admin');
      else if (user.role === 'mentor') navigate('/mentor');
      else navigate('/dashboard');
    }
  }, [user, navigate]);

  const features = settings?.features || [];
  const communityBoxes = Array.isArray(settings?.community_boxes) ? settings.community_boxes : [];
  const aboutImage =
    settings?.about_image_upload ||
    settings?.about_image_url ||
    'https://images.unsplash.com/photo-1651495835942-ebe13feacaaa?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwyfHxzdHVkZW50JTIwc3R1ZHlpbmclMjBuaWdodHxlbnwwfHx8fDE3NzU4MjM3MTl8MA&ixlib=rb-4.1.0&q=85';

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Header transparent />

      <HeroSection settings={settings} navigate={navigate} />

      <section className="py-24 px-6 bg-[#0A0A0A]" data-testid="features-section">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#B026FF]" style={{ fontFamily: 'Outfit, sans-serif' }}>Özellikler</span>
            <h3 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold mt-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Neden Karga Akademi?</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((f, i) => {
              const IconComp = iconMap[f.icon] || Sparkles;
              return (
                <div key={i} className="bg-[#0A0A0A] border border-white/10 p-8 rounded-lg transition-all hover:border-[#B026FF]/50 hover:shadow-lg hover:shadow-[#B026FF]/10" data-testid={`feature-card-${i}`}>
                  <div className="w-12 h-12 bg-[#B026FF]/10 rounded-lg flex items-center justify-center mb-4">
                    <IconComp className="w-6 h-6 text-[#B026FF]" strokeWidth={1.5} />
                  </div>
                  <h4 className="text-xl font-semibold mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>{f.title}</h4>
                  <p className="text-[#A1A1AA] leading-relaxed text-sm">{f.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-24 px-6" data-testid="about-section">
        <div className="container mx-auto max-w-6xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <img src={aboutImage} alt="Student" className="rounded-lg shadow-2xl" />
            </div>
            <div>
              <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#B026FF]" style={{ fontFamily: 'Outfit, sans-serif' }}>Hakkımızda</span>
              <h3 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold mt-4 mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>Başarıya Giden Yolda Yanındayız</h3>
              <p className="text-base leading-relaxed text-[#A1A1AA] mb-6">
                {(settings?.about_text || "").split('\n')[0] || "Karga Akademi, öğrencilerin hedeflerine ulaşması için özel olarak tasarlanmış bir premium mentorluk platformudur."}
              </p>
              <Button onClick={() => navigate('/about')} variant="outline" className="border-[#B026FF]/50 text-[#B026FF] hover:bg-[#B026FF]/10" data-testid="about-more-btn">Daha Fazla</Button>
            </div>
          </div>
        </div>
      </section>

      {communityBoxes.length > 0 && (
        <section className="py-24 px-6 bg-[#07111E]">
          <div className="container mx-auto max-w-5xl">
            <div className="mb-10">
              <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#60A5FA]" style={{ fontFamily: 'Outfit, sans-serif' }}>Topluluk</span>
              <h3 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold mt-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Karga topluluğuna bağlan</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {communityBoxes.map((item, index) => (
                <button
                  key={`${item.title}-${index}`}
                  type="button"
                  onClick={() => item.whatsapp_url && window.open(item.whatsapp_url, '_blank')}
                  className="text-left rounded-2xl border border-emerald-500/15 bg-[#0C1827] px-5 py-4 hover:border-emerald-400/40 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className="h-12 w-12 rounded-full bg-emerald-500 flex items-center justify-center text-white text-xl font-black">↗</div>
                    <div>
                      <p className="text-lg font-semibold text-white">{item.title}</p>
                      <p className="text-sm text-[#94A3B8]">{item.description}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}
